/**
 * useMediaSearch — Debounced media content search for the command palette.
 *
 * Wraps `useContentSearch` with:
 * - 300ms debounce to avoid rapid-fire API calls
 * - Permission gating (admin + WSD enabled + at least one server configured)
 * - Minimum query length (2 characters)
 * - Result flattening from per-server arrays into a single list
 *
 * Media search is async and runs alongside the instant client-side page search.
 */

import { useEffect, useMemo, useRef } from "react";
import { useContentSearch, type ContentSearchItem } from "./useServerProxy.js";
import { useMe } from "./useMe.js";
import { useWSDSettings } from "./useWSDSettings.js";
import { isAdminUser } from "../lib/permissions.js";

const DEBOUNCE_MS = 300;
const MEDIA_SEARCH_LIMIT = 8;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MediaSearchResult {
  id: string;
  name: string;
  type: string;
  serverType: string;
  seriesName: string | null;
  seasonNumber: number | null;
  episodeNumber: number | null;
  productionYear: number | null;
  overview: string | null;
  imageTag: string | null;
  communityRating: number | null;
  genres: string[];
  quality: string | null;
  isHdr: boolean;
  runTimeTicks: number | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMediaSearch(query: string) {
  const { user, appConfig } = useMe();
  const { settings } = useWSDSettings();
  const contentSearch = useContentSearch();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Gate conditions
  const isAdmin = isAdminUser(user);
  const isWsdEnabled = appConfig?.features?.wsdashboardEnabled ?? false;
  const hasServers = !!(settings?.embyConfigured || settings?.jellyfinConfigured);
  const mediaEnabled = isAdmin && isWsdEnabled && hasServers;

  // Debounced search trigger
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();

    if (!mediaEnabled || trimmed.length < 2) {
      contentSearch.clear();
      return;
    }

    debounceRef.current = setTimeout(() => {
      void contentSearch.search(trimmed, MEDIA_SEARCH_LIMIT);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // contentSearch.search and contentSearch.clear are stable useCallback refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, mediaEnabled]);

  // Flatten multi-server results into a single array
  const mediaResults = useMemo<MediaSearchResult[]>(() => {
    if (!mediaEnabled) return [];

    const flat: MediaSearchResult[] = [];
    for (const server of contentSearch.servers) {
      if (server.error) continue;
      for (const item of server.items) {
        flat.push(flattenItem(item, server.serverType));
      }
    }
    return flat.slice(0, MEDIA_SEARCH_LIMIT);
  }, [contentSearch.servers, mediaEnabled]);

  return {
    mediaResults,
    mediaLoading: contentSearch.loading,
    mediaError: contentSearch.error,
    mediaEnabled,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flattenItem(item: ContentSearchItem, serverType: string): MediaSearchResult {
  return {
    id: item.id,
    name: item.name,
    type: item.type,
    serverType,
    seriesName: item.seriesName,
    seasonNumber: item.seasonNumber,
    episodeNumber: item.episodeNumber,
    productionYear: item.productionYear,
    overview: item.overview,
    imageTag: item.imageTag,
    communityRating: item.communityRating,
    genres: item.genres,
    quality: item.quality,
    isHdr: item.isHdr,
    runTimeTicks: item.runTimeTicks,
  };
}
